import { combineEpics } from "redux-observable";
import { concat as concatStatic, Observable, of } from "rxjs";
import { catchError, filter, mergeMap } from "rxjs/operators";

import { Action, actionCreators, isOfAction } from "../actions";
import {
    getFitRooms,
    getFlattenRoomViews,
    getNextRoomsToClose,
    RoomType,
    RoomView,
} from "../reducers/rooms";
import { Epic, RootStateObservable } from "../utils/types";

const notOpen$ = <A>(roomId: string, state$: RootStateObservable, action: A) => {
    const views = getFlattenRoomViews(state$.value);
    const notOpen = !views.some(x => x.roomId === roomId && x.type === RoomType.REAL_TIME);
    if (notOpen) {
        return of(action);
    }
    return of<never>();
};

const closeRoomSucceeded = (roomsToClose: RoomView[]) => {
    if (roomsToClose.length) {
        return of(actionCreators.closeRoomSucceeded(roomsToClose));
    }
    return of<never>();
};

const open = (roomView: RoomView, state$: RootStateObservable) =>
    of(actionCreators.openRoomSucceeded({ roomView }));

const closeIfNeeded$ = (roomViewToOpen: RoomView, state$: RootStateObservable) => {
    const rooms = getFlattenRoomViews(state$.value);

    if (rooms.size === getFitRooms(state$.value)) {
        const rooms = getNextRoomsToClose(state$.value);
        const [room1, room2] = rooms.toArray();
        let room = room1;
        if (room1.roomId === roomViewToOpen.roomId && room1.type === RoomType.REAL_TIME) {
            // we are trying to close the same room we are opening search for, let's not do that
            room = room2;
        }
        if (room) {
            return closeRoomSucceeded([room]);
        }
    }
    return of<never>();
};

const onOpen: Epic = (action$, state$, extra) =>
    action$.pipe(
        filter(isOfAction(actionCreators.openRoomRequested)),
        mergeMap(({ payload: { roomView } }) =>
            concatStatic(closeIfNeeded$(roomView, state$), open(roomView, state$))
        )
    );

const onClose: Epic = (action$, state$, extra) =>
    action$.pipe(
        filter(isOfAction(actionCreators.closeRoomRequested)),
        mergeMap(({ payload }) => {
            const views = getFlattenRoomViews(state$.value);
            return closeRoomSucceeded(payload.filter(x => views.contains(x)));
        })
    );

const fluxListener: Epic = (action$, state$, { App }) =>
    action$.pipe(
        mergeMap(
            (action): Observable<Action> => {
                if (action.type === actionCreators.JoinRoom.type) {
                    // we joined a room we didn't open, let's open it
                    const roomId = action.payload;
                    return notOpen$(
                        roomId,
                        state$,
                        actionCreators.setRoomToOpen({ roomId, type: RoomType.REAL_TIME })
                    );
                } else if (action.type === actionCreators.LeaveRoom.type) {
                    // we are leaving opened room, let's close it
                    const roomId = action.payload;
                    const views = getFlattenRoomViews(state$.value).toArray();
                    const roomsToClose = views.filter(x => x.roomId === roomId);
                    return closeRoomSucceeded(roomsToClose);
                } else if (action.type === actionCreators.onRoster.type) {
                    const roomIds = action.payload.data.remove_room_ids;
                    if (!roomIds) {
                        return of();
                    }
                    const rooms = getFlattenRoomViews(state$.value).toArray();
                    const roomsToClose = rooms.filter(x => roomIds.includes(x.roomId));
                    return closeRoomSucceeded(roomsToClose);
                }
                return of<never>();
            }
        ),
        catchError(err => {
            console.error("service failed", err);
            return of<never>();
        })
    );

export const roomsToggleEpic = combineEpics(onOpen, onClose, fluxListener);
